import { addFieldVersion, applyFieldVersion, removeFieldVersion, writeFieldVersion } from './versions.js';
import { overlappingAnswerPairs } from './answer-overlap.js';
import { answerHalfWidths } from './answer-tolerance.js';
import { optionalOutcome, participationOutcome, SET_ANSWER_TRIGGER } from './grading-mode.js';
import { coverageGaps, isIntervalFullyCovered, mergeCoverageIntervals, rangesOverlap } from './interval-coverage.js';
import { TILT_OFF_AXIS_CUTOFF_DEG } from './gravity-readings.js';
import { isNoSensorData, NO_SENSOR_DATA_TRIGGER } from './sensor-health.js';
import { matchesValueTrigger as matchesValueTriggerShared, parseValueTrigger, triggerToBoundedInterval as triggerToBoundedIntervalShared } from './value-trigger.js';
import { resolveScenarioValueSetup } from './scenario.js';
const EPS = (n) => 1e-9 * Math.max(1, Math.abs(n));
/**
 * Bounded, like Rotation's and unlike numerical.ts's/slider.ts's ±Infinity, and for the same
 * reason: a bounded domain is what makes the coverage arithmetic terminate, so `lt:30` covers the
 * floor up to 30 rather than everything below it.
 */
export const TILT_DOMAIN_LO = -90;
export const TILT_DOMAIN_HI = 90;
/** This block's adapter onto the shared trigger vocabulary. The epsilon is Rotation's own `1e-9`,
 *  safe here for the same reason: a domain capped at 90 never holds values where it is a
 *  meaningful fraction. */
function matchesValueTrigger(trigger, value) {
    return matchesValueTriggerShared(trigger, value, EPS);
}
function triggerToBoundedInterval(trigger) {
    return triggerToBoundedIntervalShared(trigger, TILT_DOMAIN_LO, TILT_DOMAIN_HI);
}
const isIntervalFullyCoveredEps = (t, c) => isIntervalFullyCovered(t, c, EPS);
/** A trigger's span as COVERAGE — `exact` contributes none, since a point never closes a gap. */
function tiltTriggerSpan(trigger) {
    if (parseValueTrigger(trigger)?.type === 'exact')
        return null;
    return triggerToBoundedInterval(trigger);
}
/**
 * The accepted range(s) the condition implies, as intervals bounded to the domain.
 *
 * Multiple exact/between answers are OR'd — matching any one is correct. The band is CLAMPED to
 * the domain rather than wrapped: an answer of 88 ± 5 accepts 83 to 90 and stops, because nothing
 * leans further than straight over. Rotation wraps instead, since on a loop the overshoot lands
 * somewhere real.
 */
export function computeTiltAcceptedRanges(config) {
    const { conditionType, answers, betweenAnswers, threshold } = config;
    const DLO = TILT_DOMAIN_LO, DHI = TILT_DOMAIN_HI;
    const closed = { loInclusive: true, hiInclusive: true };
    if (conditionType === 'exact') {
        return answers.filter(a => Number.isFinite(a.value)).map(a => {
            const { below, above } = answerHalfWidths(a, config);
            return { lo: Math.max(DLO, a.value - below), hi: Math.min(DHI, a.value + above), ...closed };
        }).filter(r => r.hi >= r.lo);
    }
    if (conditionType === 'between') {
        return betweenAnswers.filter(a => Number.isFinite(a.low) && Number.isFinite(a.high))
            .map(a => ({ lo: a.low, hi: a.high, ...closed }));
    }
    if (conditionType === 'lt' || conditionType === 'lte') {
        return [{ lo: DLO, hi: threshold, loInclusive: true, hiInclusive: conditionType === 'lte' }];
    }
    return [{ lo: threshold, hi: DHI, loInclusive: conditionType === 'gte', hiInclusive: true }];
}
/** Is this value within the accepted condition? Handles lt/gt's exclusive boundary explicitly. */
export function isTiltWithinAcceptedRange(value, config) {
    if (config.conditionType === 'lt')
        return value < config.threshold;
    if (config.conditionType === 'lte')
        return value <= config.threshold;
    if (config.conditionType === 'gt')
        return value > config.threshold;
    if (config.conditionType === 'gte')
        return value >= config.threshold;
    return computeTiltAcceptedRanges(config).some(r => value >= r.lo - EPS(r.lo) && value <= r.hi + EPS(r.hi));
}
/**
 * The targeted states a READING fires outside graded: the value triggers it matches, plus Set
 * Answer when it lands inside the accepted band.
 *
 * Shared by survey and optional so the two cannot drift. Set Answer resolves against the band
 * derivation the correct card already uses, which keeps it in step with an edited answer.
 */
function participationMatches(reading, states, config) {
    return states.filter(st => st.type !== 'targeted' ? false
        : st.trigger === SET_ANSWER_TRIGGER ? isTiltWithinAcceptedRange(reading, config)
            : matchesValueTrigger(st.trigger, reading));
}
/**
 * The feedback states a student's tilt measurement fires.
 *
 * Rotation's sequence, including its off-axis gate — which Tilt does have a counterpart for,
 * though it took removing and restoring to see it. The single-axis draft genuinely had no axis to
 * be off; the two-axis one does, because a phone held diagonally splits the slope between its
 * components and reports a plausible understatement.
 *
 * 1. optional -> the catch-all, whatever happened. A block nobody grades has no problem to route.
 * 2. no sensor data -> the device failed with no way round it. Above the blank check because
 *    `touched` is false either way and this is the more specific case.
 * 3. untouched -> unanswered.
 * 4. sensor readings only: held too far off-axis -> poor_axis.
 * 5. survey -> targeted states, else any_answer.
 * 6. graded -> correct (terminal), else targeted, else incorrect.
 */
/**
 * This block's answer setup for the scenario in force — the value family's shared rule, with
 * only this block's own field list and fail-closed shape supplied.
 *
 * Empty answer lists are what "no row" means: nothing can be correct, which is the same
 * fail-closed choice the choice family makes by going all-neutral.
 */
export function resolveTiltScenario(d, scenarioId) {
    return resolveScenarioValueSetup(d, scenarioId, TILT_VERSION_KEYS, { answers: [], betweenAnswers: [] });
}
export function evaluateTilt(answer, health, rawBlockData, 
/** The leader's variant in force — see `resolveScenarioValueSetup`. */
scenarioId) {
    const blockData = resolveTiltScenario(rawBlockData, scenarioId);
    const { touched, tiltDegrees } = answer;
    const { conditionType, answers, betweenAnswers, toleranceSymmetry, threshold, gradingMode, feedback } = blockData;
    const acceptedConfig = { conditionType, answers, betweenAnswers, toleranceSymmetry, threshold };
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(feedback.states);
    }
    if (isNoSensorData(health)) {
        const noData = feedback.states.find(s => s.type === 'default' && s.trigger === NO_SENSOR_DATA_TRIGGER);
        return noData ? [noData] : [];
    }
    if (!touched) {
        const unanswered = feedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    /*
     * Held at an angle between the phone's two axes, so the slope is split between them and the
     * number is a quiet understatement — the worst kind of wrong, because nothing looks amiss.
     *
     * GATED rather than graded, matching GPS's poor accuracy and Rotation's off-plane: mis-holding
     * the phone is a real mistake a teacher wants to catch and correct, and marking it WRONG would
     * score a measuring error as a wrong answer.
     *
     * SENSOR answers only, for the same reason compass skips its gates for a typed one: the gate
     * judges an instrument, and there is no instrument behind a number somebody typed.
     */
    if (answer.method === 'sensor'
        && health.offAxisDegrees != null
        && health.offAxisDegrees > TILT_OFF_AXIS_CUTOFF_DEG) {
        const poorAxis = feedback.states.find(s => s.type === 'default' && s.trigger === 'poor_axis');
        return poorAxis ? [poorAxis] : [];
    }
    const targetedForReading = () => participationMatches(tiltDegrees, feedback.states, acceptedConfig);
    if (gradingMode === 'survey') {
        return participationOutcome(feedback.states, 'survey', targetedForReading());
    }
    if (isTiltWithinAcceptedRange(tiltDegrees, acceptedConfig)) {
        const correct = feedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    const matched = targetedForReading();
    if (matched.length > 0)
        return matched;
    const incorrect = feedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
// ─── Authoring-time reachability analysis ──────────────────────────────────────
/**
 * Whether Default Incorrect is genuinely unreachable — every point in the domain is covered by
 * the accepted condition(s) or by some value feedback.
 *
 * Rotation defines this via its gaps because a wrapped answer's coverage can land as two pieces
 * that are contiguous around the seam, so "exactly one merged interval" is the wrong test there.
 * Tilt has no seam and could use the simpler test, but is kept defined in terms of the gaps for
 * the same reason the others are: the two must agree, and one definition cannot disagree with
 * itself.
 */
export function isTiltDefaultIncorrectUnreachable(acceptedRanges, feedbackTriggers) {
    return computeTiltDefaultIncorrectGaps(acceptedRanges, feedbackTriggers).length === 0;
}
/** The parts of the domain that actually reach Default Incorrect. Formatting is the caller's job. */
export function computeTiltDefaultIncorrectGaps(acceptedRanges, feedbackTriggers) {
    const merged = mergeCoverageIntervals(acceptedRanges, feedbackTriggers, tiltTriggerSpan, EPS);
    return coverageGaps(merged, TILT_DOMAIN_LO, TILT_DOMAIN_HI, EPS);
}
/**
 * Whether a targeted trigger's entire condition falls inside the accepted range(s), so it can
 * never fire — Correct is checked first and is terminal.
 *
 * Both sides carry their own boundaries. Closing the ACCEPTED side would over-approximate
 * coverage: with Correct = "< 5", a targeted state at exactly 5 is a reachable wrong answer.
 */
export function isTiltFeedbackFullySubsumed(trigger, acceptedRanges) {
    const target = triggerToBoundedInterval(trigger);
    if (!target)
        return false;
    return isIntervalFullyCoveredEps(target, acceptedRanges);
}
/**
 * Whether a targeted trigger's interval intersects the accepted range(s) at all — strictly weaker
 * than full subsumption. A shared boundary is real overlap only when both sides admit that point,
 * so Correct = "<= 10" and a trigger of "> 10" are perfect complements rather than an overlap.
 */
export function doesTiltFeedbackOverlapAccepted(trigger, acceptedRanges) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed || parsed.type === 'exact')
        return false;
    const target = triggerToBoundedInterval(trigger);
    if (!target)
        return false;
    return acceptedRanges.some(r => {
        if (target.hi < r.lo || r.hi < target.lo)
            return false;
        if (target.hi === r.lo)
            return target.hiInclusive && r.loInclusive;
        if (r.hi === target.lo)
            return r.hiInclusive && target.loInclusive;
        return true;
    });
}
/**
 * Pairs of ACCEPTED ANSWERS covering some of the same tilts.
 *
 * A warning, never an error: overlapping answers still grade correctly, since acceptance is their
 * union. What is wrong is the authoring, where two rows that look different mean one thing.
 *
 * Unlike Rotation this needs no seam-awareness — there is no second spelling of an angle here, so
 * a plain numeric comparison is complete rather than merely usually right.
 */
export function findTiltOverlappingAnswerPairs(config) {
    const { conditionType, answers, betweenAnswers } = config;
    const DLO = TILT_DOMAIN_LO, DHI = TILT_DOMAIN_HI;
    const perAnswer = conditionType === 'exact'
        ? answers.map(a => {
            const { below, above } = answerHalfWidths(a, config);
            return { id: a.id, intervals: [{ lo: Math.max(DLO, a.value - below), hi: Math.min(DHI, a.value + above) }] };
        })
        : conditionType === 'between'
            ? betweenAnswers.map(a => ({ id: a.id, intervals: [{ lo: Math.min(a.low, a.high), hi: Math.max(a.low, a.high) }] }))
            : [];
    return overlappingAnswerPairs(perAnswer);
}
/**
 * Pairs of authored targeted triggers whose ranges overlap each other — likely unintentional.
 * `exact` triggers are excluded, mirroring the other value blocks: a specific callout nested
 * inside a broader band is a common deliberate pattern, not a mistake.
 */
export function findTiltOverlappingFeedbackPairs(feedbackTriggers) {
    const DLO = TILT_DOMAIN_LO, DHI = TILT_DOMAIN_HI;
    const parsed = feedbackTriggers
        .map(trigger => {
        const p = parseValueTrigger(trigger);
        if (!p || p.type === 'exact')
            return null;
        if (p.type === 'lt')
            return { trigger, lo: DLO, hi: p.n1, loInclusive: true, hiInclusive: false };
        if (p.type === 'lte')
            return { trigger, lo: DLO, hi: p.n1, loInclusive: true, hiInclusive: true };
        if (p.type === 'gt')
            return { trigger, lo: p.n1, hi: DHI, loInclusive: false, hiInclusive: true };
        if (p.type === 'gte')
            return { trigger, lo: p.n1, hi: DHI, loInclusive: true, hiInclusive: true };
        return { trigger, lo: Math.min(p.n1, p.n2), hi: Math.max(p.n1, p.n2), loInclusive: true, hiInclusive: true };
    })
        .filter((x) => x !== null);
    const pairs = [];
    for (let i = 0; i < parsed.length; i++) {
        for (let j = i + 1; j < parsed.length; j++) {
            const a = parsed[i], b = parsed[j];
            if (rangesOverlap(a, b))
                pairs.push([a.trigger, b.trigger]);
        }
    }
    return pairs;
}
/**
 * The fields a Tilt version owns — §3.2 of `docs/question-versions.md`.
 *
 * `visibilityMode` stays shared: whether the student can see the live reading is how the
 * question is asked, not what counts as right.
 */
export const TILT_VERSION_KEYS = ['conditionType', 'answers', 'toleranceSymmetry', 'betweenAnswers', 'threshold'];
export function applyTiltVersion(data, versionId) {
    return applyFieldVersion(data, versionId, TILT_VERSION_KEYS);
}
export function writeTiltVersion(current, versionId, next) {
    return writeFieldVersion(current, versionId, next, TILT_VERSION_KEYS);
}
export function addTiltVersion(data, fromVersionId, newId) {
    return addFieldVersion(data, fromVersionId, newId, TILT_VERSION_KEYS);
}
export function removeTiltVersion(data, versionId) {
    return removeFieldVersion(data, versionId, TILT_VERSION_KEYS);
}
//# sourceMappingURL=tilt.js.map